/**
 * A link to the creative studio — the arche where people make the styles and
 * characters in their Creative Library (FiasWorks Studio; ADR-037 D8). Which
 * arche that is comes from the `creative-studio` Fias arche role an admin
 * assigns per environment, so nothing here names an arche id.
 *
 * From a Fias tool (`from`: the forge page, the Arche Builder) it opens in the
 * SAME tab and tells the studio where the person came from, so the studio can
 * offer "Return to …" and "Use in …" (ADR-037 D8, `lib/arche-opener.ts`).
 * Anywhere else — inside another arche's picker, say — it opens in a new tab,
 * so whatever the person was in the middle of stays open. While no studio is
 * assigned, or this person cannot open it, it renders nothing.
 */
import * as React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Palette } from 'lucide-react';
import { archePath } from '@/lib/arche-url';
import { withOpener, type OpenerRef } from '@/lib/arche-opener';
import { useFiasArche } from '@/hooks/useFiasArche';
import { cn } from '@/lib/utils';

interface CreativeStudioLinkProps {
  /** What the link says, given the studio's name ("Open FiasWorks Studio"). */
  children?: (studioName: string) => React.ReactNode;
  /** The Fias tool this link is on; the studio then opens here, with a way back. */
  from?: OpenerRef;
  className?: string;
}

const LINK_CLASS =
  'inline-flex items-center gap-1 font-medium text-primary underline-offset-4 hover:underline';

export function CreativeStudioLink({ children, from, className }: CreativeStudioLinkProps) {
  const { data: studio } = useFiasArche('creative-studio');
  if (!studio) return null;
  const path = `/a/${archePath({ archId: studio.archeId, slug: studio.slug })}`;
  const label = children ? children(studio.name) : `Open ${studio.name}`;
  if (from) {
    return (
      <Link
        to={withOpener(path, from)}
        className={cn(LINK_CLASS, className)}
        data-testid="creative-studio-link"
      >
        <Palette className="h-3 w-3 shrink-0" aria-hidden="true" />
        {label}
      </Link>
    );
  }
  return (
    <a
      href={path}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(LINK_CLASS, className)}
      data-testid="creative-studio-link"
    >
      {label}
      <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
      <span className="sr-only"> (opens in a new tab)</span>
    </a>
  );
}
